// Pantalla — the webapp's display settings that are the reader's own preferences on the server
// (`display_settings` category), so they follow the person to every device: collapsed reply threads
// (only when the server lets the user choose), the clock, how names are shown (unless locked), and
// link previews. The server echoes `preferences_changed`; the engine re-reads its policies on it.
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { nameFormatChoice, nameFormats, threadsChoice } from '../../../core/accountSettings';
import { timezoneLabel, timezoneOptions, type AccountTimezone } from '../../../core/accountTimezone';
import { t, tf } from '../../../i18n/i18n';
import { InlineStatus } from '../../kit/Kit';
import { ThemeGroup } from './LocalSections';
import { RadioRow, SettingsGroup, SwitchRow, useAccount, useSaver } from './parts';

export function DisplaySection() {
  const { prefs, setPrefs } = useAccount();
  const threads = useSaver();
  const clock = useSaver();
  const names = useSaver();
  const previews = useSaver();
  if (!prefs) {
    return (
      <>
        <ThemeGroup />
        <div className="st-skeleton" aria-busy="true" />
      </>
    );
  }

  const save = (saver: ReturnType<typeof useSaver>, name: string, value: string) => {
    const before = prefs;
    setPrefs({ ...prefs, display: { ...prefs.display, [name]: value } });
    saver.run(
      () => data.call('accountSavePreferences', [{ category: 'display_settings', name, value }]),
      () => setPrefs(before),
    );
  };

  const crt = threadsChoice(prefs.config.threads, prefs.display.collapsed_reply_threads);
  const nameChoice = nameFormatChoice(prefs.config.nameDisplay, prefs.config.nameDisplayLocked, prefs.display.name_format);
  // Unset = the apps' 24-hour clock (core/clockStyle); only an explicit "false" is 12-hour.
  const military = prefs.display.use_military_time !== 'false';
  const linkPreviews = prefs.display.link_previews !== 'false';

  return (
    <>
      <ThemeGroup />
      <SettingsGroup title={t('web.settings.display.threads')} save={threads.state} footer={crt.editable ? undefined : t('web.settings.display.threadsLocked')}>
        <SwitchRow
          title={t('web.settings.display.threads')}
          subtitle={t('web.settings.display.threadsHint')}
          on={crt.on}
          disabled={!crt.editable}
          onChange={(on) => save(threads, 'collapsed_reply_threads', on ? 'on' : 'off')}
        />
      </SettingsGroup>

      <SettingsGroup title={t('web.settings.display.clock')} save={clock.state}>
        <SwitchRow title={t('web.display.clock24')} subtitle={t('web.display.clock24Hint')} on={military} onChange={(on) => save(clock, 'use_military_time', on ? 'true' : 'false')} />
      </SettingsGroup>

      <TimezoneGroup />

      <SettingsGroup title={t('web.settings.display.names')} save={names.state} footer={nameChoice.editable ? undefined : t('web.settings.display.namesLocked')}>
        <div role="radiogroup" aria-label={t('web.settings.display.names')}>
          {nameFormats.map((f) => (
            <RadioRow key={f} title={t(`web.settings.display.name.${f}`)} checked={nameChoice.value === f} disabled={!nameChoice.editable} onPick={() => nameChoice.value !== f && save(names, 'name_format', f)} />
          ))}
        </div>
      </SettingsGroup>

      <SettingsGroup title={t('web.settings.display.linkPreviews')} save={previews.state} footer={t('web.settings.serverWide')}>
        <SwitchRow title={t('web.settings.display.linkPreviews')} subtitle={t('web.settings.display.linkPreviewsHint')} on={linkPreviews} onChange={(on) => save(previews, 'link_previews', on ? 'true' : 'false')} />
      </SettingsGroup>
    </>
  );
}

function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function supportedZones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf?(k: string): string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
}

/**
 * Zona horaria (shared/API.md §41.9): «Automática» follows this device (the worker re-syncs it on
 * every launch); off, a list of zones. The account's value, so read from and written to the server;
 * optimistic, restored on refusal.
 */
function TimezoneGroup() {
  const saver = useSaver();
  const [tz, setTz] = useState<AccountTimezone | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const device = deviceZone();
  useEffect(() => {
    let alive = true;
    setFailed(false);
    data.call('accountTimezone').then(
      (r) => alive && (r ? setTz(r) : setFailed(true)),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [attempt]);
  const options = useMemo(() => timezoneOptions(supportedZones(), [tz?.manual, tz?.automatic, device]), [tz?.manual, tz?.automatic, device]);

  if (!tz) {
    return (
      <SettingsGroup title={t('web.display.timezone')}>
        {failed ? <InlineStatus kind="error" text={t('web.settings.loadFailed')} onRetry={() => setAttempt((n) => n + 1)} /> : <div className="st-skeleton" aria-busy="true" />}
      </SettingsGroup>
    );
  }
  const write = (next: AccountTimezone) => {
    const before = tz;
    setTz(next);
    saver.run(
      async () => {
        const answer = await data.call('accountSetTimezone', next);
        if (answer) setTz(answer);
        return !!answer;
      },
      () => setTz(before),
      t('web.display.timezoneFailed'),
    );
  };
  const manualValue = tz.manual || tz.automatic || device;
  return (
    <SettingsGroup title={t('web.display.timezone')} save={saver.state} footer={t('web.display.timezoneFooter')}>
      <SwitchRow
        title={t('web.display.timezoneAuto')}
        subtitle={tf('web.display.timezoneAutoHint', timezoneLabel(device))}
        on={tz.useAutomatic}
        onChange={(on) => write(on ? { ...tz, useAutomatic: true, automatic: device } : { ...tz, useAutomatic: false, manual: manualValue })}
      />
      {!tz.useAutomatic ? (
        <label className="list-row st-tz-row">
          <span className="list-row-text">
            <span className="list-row-title">{t('web.display.timezonePick')}</span>
          </span>
          <select className="text-input st-tz-select" value={manualValue} onChange={(e) => write({ ...tz, manual: e.target.value })} aria-label={t('web.display.timezone')}>
            {options.map((z) => (
              <option key={z} value={z}>
                {timezoneLabel(z)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </SettingsGroup>
  );
}
